(() => {
  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let added = 0;

    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }

    return result;
  };

  const firstShippingDay = (date) => {
    const result = new Date(date);
    while (result.getDay() === 0 || result.getDay() === 6) {
      result.setDate(result.getDate() + 1);
    }
    return result;
  };

  const addCalendarDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const formatShort = (date) =>
    new Intl.DateTimeFormat(document.documentElement.lang || 'it-IT', {
      day: 'numeric',
      month: 'short'
    }).format(date);

  const formatRange = (start, end) => `${formatShort(start)} – ${formatShort(end)}`;

  const initGallery = (gallery) => {
    const main = gallery.querySelector('[data-marri-mobile-main]');
    const thumbs = [...gallery.querySelectorAll('[data-marri-mobile-thumb]')];
    if (!main || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        main.src = thumb.dataset.imageSrc;
        main.srcset = thumb.dataset.imageSrcset;
        main.alt = thumb.dataset.imageAlt || '';
        thumbs.forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      });
    });

    gallery.querySelector('[data-marri-mobile-zoom]')?.addEventListener('click', () => {
      const activeId = gallery.querySelector('[data-marri-mobile-thumb].is-active')?.dataset.mediaId;
      const nativeMedia = activeId
        ? document.querySelector(`[data-media-id$="-${CSS.escape(activeId)}"]`)
        : null;
      nativeMedia?.querySelector('button, a')?.click();
    });
  };

  const initDescription = (wrapper) => {
    const button = wrapper.querySelector('[data-marri-description-toggle]');
    const content = wrapper.querySelector('[data-marri-description-content]');
    if (!button || !content) return;

    button.addEventListener('click', () => {
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      content.classList.toggle('is-expanded', !expanded);
      button.textContent = expanded
        ? button.dataset.moreLabel
        : button.dataset.lessLabel;
    });
  };

  const initDelivery = (timeline) => {
    const today = new Date();
    const prepStart = firstShippingDay(today);
    const prepEnd = addBusinessDays(prepStart, 1);
    const deliveryStart = firstShippingDay(addCalendarDays(prepStart, 2));
    const deliveryEnd = firstShippingDay(addCalendarDays(prepStart, 5));

    timeline.querySelector('[data-marri-order-date]').textContent = formatShort(today);
    timeline.querySelector('[data-marri-prep-date]').textContent = formatRange(prepStart, prepEnd);
    timeline.querySelector('[data-marri-delivery-date]').textContent = formatRange(deliveryStart, deliveryEnd);
    timeline.querySelector('[data-marri-delivery-summary]').textContent =
      `${timeline.dataset.estimatedLabel || 'Estimated delivery'} ${formatRange(deliveryStart, deliveryEnd)}`;
  };

  const initAccordions = () => {
    document.querySelectorAll('.product-information-section .accordion-header-pi').forEach((button) => {
      button.addEventListener('click', () => {
        requestAnimationFrame(() => {
          button.setAttribute(
            'aria-expanded',
            button.closest('.accordion-item-pi')?.classList.contains('active') ? 'true' : 'false'
          );
        });
      });
    });
  };

  const initAtcPrice = (priceNode) => {
    const productInfo = priceNode.closest('.product-info-wrapper');
    const priceArea = productInfo?.querySelector('[id^="price-"], .product-price');
    if (!priceArea) return;

    const syncPrice = () => {
      const currentPrice =
        priceArea.querySelector('.price--on-sale .price-item--sale, .price-item--sale.price-item--last') ||
        priceArea.querySelector('.price-item--regular');
      const value = currentPrice?.textContent?.replace(/\s+/g, ' ').trim();
      if (value) priceNode.textContent = value;
    };

    syncPrice();
    new MutationObserver(syncPrice).observe(priceArea, {
      childList: true,
      subtree: true,
      characterData: true
    });
  };

  const initDragScroll = (track) => {
    let startX = 0;
    let startScroll = 0;
    let dragged = false;
    let pointerDown = false;
    let activePointerId = null;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      startX = event.clientX;
      startScroll = track.scrollLeft;
      dragged = false;
      pointerDown = true;
      activePointerId = event.pointerId;

      // Important: do NOT capture the pointer here. A normal desktop click
      // must remain targeted at the product link. Pointer capture starts only
      // after an actual horizontal drag is detected.
    });

    track.addEventListener('pointermove', (event) => {
      if (!pointerDown || event.pointerId !== activePointerId) return;

      const distance = event.clientX - startX;
      if (!dragged && Math.abs(distance) > 6) {
        dragged = true;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(event.pointerId);
      }

      if (!dragged) return;
      event.preventDefault();
      track.scrollLeft = startScroll - distance;
    });

    const stopDragging = (event) => {
      if (event.pointerId !== activePointerId) return;

      pointerDown = false;
      activePointerId = null;
      track.classList.remove('is-dragging');

      if (track.hasPointerCapture?.(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }

      if (event.type === 'pointercancel') dragged = false;
    };

    track.addEventListener('pointerup', stopDragging);
    track.addEventListener('pointercancel', stopDragging);
    track.addEventListener(
      'click',
      (event) => {
        if (!dragged) return;
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      },
      true
    );

    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      track.scrollBy({ left: direction * Math.max(260, track.clientWidth * 0.72), behavior: 'smooth' });
    });
  };

  const init = () => {
    document.querySelectorAll('[data-marri-mobile-gallery]').forEach(initGallery);
    document.querySelectorAll('[data-marri-description]').forEach(initDescription);
    document.querySelectorAll('[data-marri-product-delivery]').forEach(initDelivery);
    document.querySelectorAll('[data-marri-drag-scroll]').forEach(initDragScroll);
    document.querySelectorAll('[data-marri-atc-price]').forEach(initAtcPrice);
    initAccordions();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
